import { useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import Logo from 'components/App/Header/Logo'
import { mediaQueryUp } from 'components/utils/breakpoint'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import productLabels from 'i18n/productLabels'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import TextLink from 'LuxKit/TextLink'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import Group from 'components/utils/Group'

const Root = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(20)};
    padding-bottom: ${rem(20)};
  }
`

const Content = styled(LayoutContainer)`
  @media print {
    a[href]::after {
      content: none !important;
    }
  }
`

const FooterLogo = styled(Logo)`
  width: ${rem(144)};
  height: unset;
`

function MinimalFooter() {
  const intl = useIntl()
  const currentYear = new Date().getFullYear()

  const pageTypes = ['privacyPolicy', 'termsAndConditions', 'refundPolicy']

  const helpItems = getStaticPagesByType(pageTypes, true)

  const localizedLinkText = (raw: string) => {
    const descriptor = Object.values(productLabels).find(d => d.defaultMessage === raw)
    return descriptor ? intl.formatMessage(descriptor) : formatStaticAppText(intl, raw)
  }

  const footerDiv = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('tripPlannerFooter', footerDiv)

  return <Root ref={footerDiv}>
    <Content size="xl">
      <Group
        direction="vertical"
        desktopDirection="horizontal"
        horizontalAlign="center"
        desktopHorizontalAlign="start"
        verticalAlign="center"
        gap={40}
      >
        <FooterLogo />
        <Group direction="vertical" desktopDirection="horizontal" desktopHorizontalAlign="space-between" gap={8}>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: '4fcdDrLw5ZJLsrJc4OeJ',
                defaultMessage: 'Copyright © 2017 - {year} {title}.',
                description: 'Copyright notice in footer with year and company title',
              },
              {
                year: currentYear,
                title: config.title,
              },
            )}
          </BodyText>
          <Group direction="horizontal" gap={12} wrap="wrap" horizontalAlign="center">
            {helpItems.map(item => <TextLink
              key={item.type}
              size="medium"
              weight="regular"
              to={item.url?.to}
              href={item.url?.href}
              target="_blank"
            >
              {typeof item.text === 'string'
                ? localizedLinkText(item.text)
                : intl.formatMessage(item.text)}
            </TextLink>)}
          </Group>
        </Group>
      </Group>
    </Content>
  </Root>
}

export default MinimalFooter
